<!--
Copyright 2020 ODK Central Developers
See the NOTICE file at the top-level directory of this distribution and at
https://github.com/getodk/central-frontend/blob/master/NOTICE.

This file is part of ODK Central. It is subject to the license terms in
the LICENSE file found in the top-level directory of this distribution and at
https://www.apache.org/licenses/LICENSE-2.0. No part of ODK Central,
including this file, may be copied, modified, propagated, or distributed
except according to the terms contained in the LICENSE file.
-->
<template>
  <div id="form-list">
    <page-section :condensed="condensed">
      <template #heading>
        <span>{{ $t('title') }}</span>
        <button v-if="project != null && project.permits('form.create')"
          id="form-list-create-button" type="button" class="btn btn-primary"
          @click="showModal('newForm')">
          <span class="icon-plus-circle"></span>{{ $t('action.create') }}&hellip;
        </button>
      </template>
      <template #body>
        <form-table/>
        <loading :state="$store.getters.initiallyLoading(['forms'])"/>
        <p v-if="forms != null && forms.length === 0"
          class="empty-table-message">
          {{ $t('emptyTable') }}
        </p>
      </template>
    </page-section>
    <form-new v-bind="newForm" @hide="hideModal('newForm')"
      @success="afterCreate"/>
  </div>
</template>

<script>
import FormNew from './new.vue';
import FormTable from './table.vue';
import Loading from '../loading.vue';
import PageSection from '../page/section.vue';
import modal from '../../mixins/modal';
import routes from '../../mixins/routes';
import { requestData } from '../../store/modules/request';

export default {
  name: 'FormList',
  components: { FormTable, FormNew, Loading, PageSection },
  mixins: [modal(), routes()],
  props: {
    condensed: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      newForm: {
        state: false
      }
    };
  },
  // The component does not assume that this data will exist when the component
  // is created.
  computed: requestData(['project', 'forms']),
  methods: {
    afterCreate(form) {
      const path = this.formPath(form.projectId, form.xmlFormId, 'draft');
      this.$router.push(path, () => {
        this.$alert().success(this.$t('alert.create', {
          name: form.nameOrId()
        }));
      });
    }
  }
};
</script>

<i18n lang="json5">
{
  "en": {
    // This is a title shown above a section of the page.
    "title": "Forms",
    "action": {
      // This is the text of a button that is used to create a new Form.
      "create": "New"
    },
    "emptyTable": "There are no Forms to show.",
    "alert": {
      "create": "Your new Form “{name}” has been created as a Draft. Take a look at the checklist below, and when you feel it’s ready, you can publish the Form for use."
    }
  }
}
</i18n>

<!-- Autogenerated by destructure.js -->
<i18n>
{
  "cs": {
    "title": "Formuláře",
    "action": {
      "create": "Nový"
    },
    "emptyTable": "Nejsou k dispozici žádné formuláře.",
    "alert": {
      "create": "Váš nový formulář „{name}“ byl vytvořen jako koncept. Podívejte se na odškrtávací seznam níže a až budete mít pocit, že je připraven, můžete formulář publikovat k použití."
    }
  },
  "de": {
    "title": "Formulare",
    "action": {
      "create": "Neu"
    },
    "emptyTable": "Keine Formulare zum Anzeigen vorhanden.",
    "alert": {
      "create": "Ihr neues Formular \"{name}\" wurde als Entwurf erstellt. Überprüfen Sie die Checkliste unten: Wenn Sie finden, das sie abgearbeitet ist, können sie das Formular veröffentlichen."
    }
  },
  "es": {
    "title": "Formularios",
    "action": {
      "create": "Nuevo"
    },
    "emptyTable": "No hay formularios para mostrar.",
    "alert": {
      "create": "Su nuevo formulario \"{name}\" se ha creado como un borrador. De un vistazo a la lista de verificación a continuación, y cuando sienta que está lista, puede publicar el formulario para su uso."
    }
  },
  "fr": {
    "title": "Formulaires",
    "action": {
      "create": "Nouveau"
    },
    "emptyTable": "Il n'y a pas de formulaire à montrer.",
    "alert": {
      "create": "Votre nouveau formulaire \"{name}\" a été créé en tant qu'ébauche. Jetez un œil à la liste de contrôle ci-dessous, et quand vous pensez que c'est prêt, vous pouvez publier le formulaire pour utilisation."
    }
  }
}
</i18n>
